// Pure display logic for the Copy Job diagnostics panel.
//
// Kept in its own module (like jobDates.js) so it can be unit-tested directly: the
// panel component imports UI primitives through the "@/" alias, which the test
// runner does not resolve.
//
// Nothing here performs I/O. Every label is derived from the job payload the Jobs
// page already fetched, so rendering diagnostics never causes an API call — let
// alone Telegram traffic. JobDiagnostics.test.js asserts that, and fails if a
// fetch/axios/useEffect ever appears in this module or in the panel.

export const stamp = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleString();
};

export const accountTone = (a) => {
  if (!a?.account_id) return { tone: "bad", text: "No account assigned" };
  if (!a.loaded) return { tone: "bad", text: "Account record missing" };
  if (a.ready) return { tone: "ok", text: `Ready — ${a.label}` };
  if (a.recovering) return { tone: "warn", text: `Reconnecting — ${a.label}` };
  if (a.runtime_status === "needs_reverification") return { tone: "bad", text: `Session revoked — re-verify ${a.label}` };
  if (a.runtime_status === "session_error") return { tone: "bad", text: `Session could not start — re-verify ${a.label}` };
  if (a.runtime_status === "disconnected") return { tone: "warn", text: `Paused — ${a.label}` };
  if (!a.has_session) return { tone: "bad", text: `No saved session — sign in ${a.label}` };
  return { tone: "warn", text: `Starting up — ${a.label}` };
};

// What the last stored validation says about one channel. Used only when the live
// peer cache has nothing to offer — e.g. straight after a backend restart, when
// every channel reads "unchecked" even for a job whose destination is known bad.
export const persistedChannelVerdict = (c, role, validation) => {
  if (!validation || !c?.channel) return null;
  const failCode = role === "source" ? "SOURCE_NOT_ACCESSIBLE" : "DESTINATION_NOT_ACCESSIBLE";
  const at = stamp(validation.checked_at);
  const from = at ? ` Recorded by the last validation at ${at}.` : "";
  // The verdict names the offending channel in its reason, so only the channel that
  // actually failed is marked — the others stay genuinely unknown.
  if (validation.code === failCode
      && String(validation.reason || "").includes(String(c.channel))) {
    return {
      tone: "bad",
      text: `Not accessible — ${c.channel}`,
      title: `${validation.reason}${from}`,
    };
  }
  // READY means every channel resolved when validation last ran.
  if (validation.state === "READY") {
    return {
      tone: "ok",
      text: `Reachable — ${c.channel}`,
      title: `Reachable when this job was last validated.${from}`,
    };
  }
  return null;
};

// Display precedence: a live cache result always wins; otherwise the persisted
// verdict; only when neither exists do we admit we do not know.
export const channelTone = (c, role, validation) => {
  if (!c || c.state === "unset") return { tone: "bad", text: `No ${role} channel set` };
  if (c.state === "resolved") return { tone: "ok", text: `Reachable — ${c.channel}` };
  if (c.state === "not_accessible") {
    return {
      tone: "bad",
      text: `${role === "source" ? "Source" : "Destination"} not accessible by assigned account — ${c.channel}`,
    };
  }
  return persistedChannelVerdict(c, role, validation)
    || { tone: "unknown", text: `Not checked yet — ${c.channel}` };
};

// A destination the persisted verdict blames ranks with the live failures, not with
// the merely-unchecked ones, so the panel surfaces the one actually stopping the job.
export const worstDestination = (destinations, validation) => {
  const list = destinations || [];
  const blamed = (x) => validation?.code === "DESTINATION_NOT_ACCESSIBLE"
    && String(validation.reason || "").includes(String(x.channel));
  return list.find((x) => x.state === "not_accessible")
    || list.find(blamed)
    || list.find((x) => x.state === "unchecked")
    || list[0];
};
